'use client';

import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import PageTitle from '@/components/layout/PageTitle';
import { routeViewPermissions } from '@/domain/permissions/helpers/routePermissions';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { isPortalRoute, PORTAL_PATH } from '@/domain/portal/helpers/sessionAudience';
import { usePortalAcesso } from '@/domain/portal/hooks/usePortalAcesso';
import { authStore } from '@/infrastructure/store/authStore';

import { useFooterItems, useNavigationItems } from './navigation';

/** Always reachable, and the page that explains the product: the last resort way out. */
const SAIDA_DE_ULTIMO_CASO = '/ajuda';

interface IAcessoDaRotaProps {
  children: React.ReactNode;
}

/**
 * Stands between the shell and the screen. The brand buyer only reaches the Portal: any other
 * route sends them there, before the screen mounts and fires requests the API would refuse.
 * Factory staff reach the screens their role in the account allows (the dashboard permissions,
 * `routeViewPermissions`); any other route renders a sentence instead of the page.
 *
 * It sits in the LAYOUT and not in each page on purpose: a screen created tomorrow is covered
 * without anyone remembering it. The screen is NOT the security, the server refuses the data
 * with 403 either way; this is here so a person who follows an old link reads what happened and
 * who to ask, instead of a screen full of errors.
 */
export function AcessoDaRota({ children }: IAcessoDaRotaProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const isAuthenticated = useStore(authStore, (s) => s.isAuthenticated);
  const { audience, isStaff, lastError, load } = usePortalAcesso();
  const { ready, canAny } = usePermissions();
  // The way out is the first screen she DOES reach, never a fixed route: sending her to the
  // Início she also cannot see would be a button that leads to this same wall.
  const items = useNavigationItems();
  const footerItems = useFooterItems();
  const saida = items[0]?.to ?? footerItems[0]?.to ?? SAIDA_DE_ULTIMO_CASO;
  const foraDoPortal = audience === 'brandBuyer' && !isPortalRoute(pathname);

  useEffect(() => {
    if (foraDoPortal) {
      router.replace(PORTAL_PATH);
    }
  }, [foraDoPortal, router]);

  // Before the login the AuthGuard below is what brings the session in.
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (audience === 'failed') {
    return (
      <div className="flex min-h-full items-center justify-center">
        <NoDataFound
          variant="error"
          size="lg"
          title={t('acesso.falhou.titulo')}
          // The API's own sentence when it refused: "choose an account in the user menu" is
          // something she can act on, while "try again in a moment" never clears that 403.
          description={lastError !== '' ? lastError : t('acesso.falhou.explica')}
          action={{ label: t('acesso.falhou.tentar'), onClick: load, icon: 'refresh' }}
        />
      </div>
    );
  }

  const idsDaRota = isStaff ? routeViewPermissions(pathname) : null;

  // Until the API says who is signed in (and, for staff, what their role allows), no screen
  // mounts: a refused screen would only collect 403s, and a gated button would show then vanish.
  if (audience === 'pending' || foraDoPortal || (idsDaRota !== null && !ready)) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <LoadingSpinner size={6} color="foreground-faint" />
      </div>
    );
  }

  if (idsDaRota === null || canAny(idsDaRota)) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-y-5 pb-8">
      <PageTitle title={t('acesso.semAcesso.paginaTitulo')} headline={t('acesso.semAcesso.subtitulo')} />
      <NoDataFound
        variant="permission"
        size="lg"
        title={t('acesso.semAcesso.titulo')}
        description={t('acesso.semAcesso.explica')}
        action={{ label: t('acesso.semAcesso.voltar'), onClick: () => router.push(saida), icon: 'arrow_forward' }}
      />
    </div>
  );
}
